import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { maintenanceRequestsApiService } from '../api/maintenance-requests-api';
import {
  MaintenanceRequest,
  MaintenanceRequestStats,
  MaintenanceRequestCreateDTO,
  MaintenanceRequestUpdateDTO,
  MaintenanceRequestFilters,
} from '../types/maintenance-request.types';

// Estado inicial
interface MaintenanceRequestsState {
  requests: MaintenanceRequest[];
  currentRequest: MaintenanceRequest | null;
  stats: MaintenanceRequestStats | null;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: MaintenanceRequestFilters;
}

const initialState: MaintenanceRequestsState = {
  requests: [],
  currentRequest: null,
  stats: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  filters: {},
};

// Async Thunks
export const fetchMaintenanceRequests = createAsyncThunk(
  'maintenanceRequests/fetchMaintenanceRequests',
  async (filters: MaintenanceRequestFilters = {}, { rejectWithValue }) => {
    try {
      const response = await maintenanceRequestsApiService.getMaintenanceRequests(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar solicitudes de mantenimiento'
      );
    }
  }
);

export const fetchMaintenanceRequest = createAsyncThunk(
  'maintenanceRequests/fetchMaintenanceRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceRequestsApiService.getMaintenanceRequestById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar solicitud de mantenimiento'
      );
    }
  }
);

export const fetchMaintenanceRequestStats = createAsyncThunk(
  'maintenanceRequests/fetchMaintenanceRequestStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await maintenanceRequestsApiService.getMaintenanceRequestStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadísticas'
      );
    }
  }
);

export const createMaintenanceRequest = createAsyncThunk(
  'maintenanceRequests/createMaintenanceRequest',
  async (data: MaintenanceRequestCreateDTO, { rejectWithValue }) => {
    try {
      const response = await maintenanceRequestsApiService.createMaintenanceRequest(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear solicitud de mantenimiento'
      );
    }
  }
);

export const updateMaintenanceRequest = createAsyncThunk(
  'maintenanceRequests/updateMaintenanceRequest',
  async (
    { id, data }: { id: string; data: MaintenanceRequestUpdateDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceRequestsApiService.updateMaintenanceRequest(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar solicitud de mantenimiento'
      );
    }
  }
);

export const deleteMaintenanceRequest = createAsyncThunk(
  'maintenanceRequests/deleteMaintenanceRequest',
  async (id: string, { rejectWithValue }) => {
    try {
      await maintenanceRequestsApiService.deleteMaintenanceRequest(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar solicitud de mantenimiento'
      );
    }
  }
);

// Slice
const maintenanceRequestsSlice = createSlice({
  name: 'maintenanceRequests',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<MaintenanceRequestFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: state => {
      state.filters = {};
    },
    setCurrentRequest: (state, action: PayloadAction<MaintenanceRequest | null>) => {
      state.currentRequest = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.pagination.page = action.payload.page;
      state.pagination.limit = action.payload.limit;
    },
  },
  extraReducers: builder => {
    // Fetch maintenance requests
    builder
      .addCase(fetchMaintenanceRequests.pending, state => {
        if (state.requests.length === 0) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMaintenanceRequests.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.requests = payload.requests || [];
        
        const newPage = payload.page || 1;
        const newLimit = payload.limit || 10;
        const newTotal = payload.total || 0;
        const newTotalPages = payload.totalPages || 1;
        
        if (
          state.pagination.page !== newPage ||
          state.pagination.limit !== newLimit ||
          state.pagination.total !== newTotal ||
          state.pagination.totalPages !== newTotalPages
        ) {
          state.pagination = {
            page: newPage,
            limit: newLimit,
            total: newTotal,
            totalPages: newTotalPages,
          };
        }
      })
      .addCase(fetchMaintenanceRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch maintenance request
    builder
      .addCase(fetchMaintenanceRequest.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.currentRequest = action.payload as MaintenanceRequest;
      })
      .addCase(fetchMaintenanceRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch stats
    builder
      .addCase(fetchMaintenanceRequestStats.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRequestStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload as MaintenanceRequestStats;
      })
      .addCase(fetchMaintenanceRequestStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create maintenance request
    builder
      .addCase(createMaintenanceRequest.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false;
        const newRequest = action.payload as MaintenanceRequest;
        state.requests.unshift(newRequest);
        state.pagination.total += 1;
        if (state.stats) {
          state.stats.total += 1;
          if (newRequest.status === 'pending') {
            state.stats.pending += 1;
          }
          if (newRequest.priority === 'urgent') {
            state.stats.urgent += 1;
          }
        }
      })
      .addCase(createMaintenanceRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update maintenance request
    builder
      .addCase(updateMaintenanceRequest.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false;
        const updatedRequest = action.payload as MaintenanceRequest;
        const index = state.requests.findIndex(
          request => request.id === updatedRequest.id
        );
        if (index !== -1) {
          state.requests[index] = updatedRequest;
        }
        if (state.currentRequest?.id === updatedRequest.id) {
          state.currentRequest = updatedRequest;
        }
      })
      .addCase(updateMaintenanceRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete maintenance request
    builder
      .addCase(deleteMaintenanceRequest.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMaintenanceRequest.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = state.requests.filter(
          request => request.id !== action.payload
        );
        state.pagination.total -= 1;
        if (state.currentRequest?.id === action.payload) {
          state.currentRequest = null;
        }
        if (state.stats) {
          state.stats.total -= 1;
        }
      })
      .addCase(deleteMaintenanceRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFilters,
  clearFilters,
  setCurrentRequest,
  clearError,
  setPagination,
} = maintenanceRequestsSlice.actions;

// Selectors
export const selectMaintenanceRequests = (state: { maintenanceRequests: MaintenanceRequestsState }) =>
  state.maintenanceRequests.requests;
export const selectCurrentMaintenanceRequest = (state: { maintenanceRequests: MaintenanceRequestsState }) =>
  state.maintenanceRequests.currentRequest;
export const selectMaintenanceRequestStats = (state: { maintenanceRequests: MaintenanceRequestsState }) =>
  state.maintenanceRequests.stats;
export const selectMaintenanceRequestsLoading = (state: { maintenanceRequests: MaintenanceRequestsState }) =>
  state.maintenanceRequests.loading;
export const selectMaintenanceRequestsError = (state: { maintenanceRequests: MaintenanceRequestsState }) =>
  state.maintenanceRequests.error;
export const selectMaintenanceRequestsPagination = (state: { maintenanceRequests: MaintenanceRequestsState }) =>
  state.maintenanceRequests.pagination;
export const selectMaintenanceRequestsFilters = (state: { maintenanceRequests: MaintenanceRequestsState }) =>
  state.maintenanceRequests.filters;

export default maintenanceRequestsSlice.reducer;
